/**
 * AgentFlow Evaluation Page
 *
 * Public page (no auth required) for submitting evaluation forms.
 * Renders dynamic forms driven by backend JSON schema (response_schema).
 * Supports flat fields, nested object sections, and conditional visibility.
 *
 * URL: /eval/:matter_id/:approval_id
 */
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  useAgentflowGetEvalFormQuery,
  useAgentflowSubmitEvalFormMutation,
} from '@/services/api/generated/agentflowEvalService';

interface SchemaProperty {
  type?: string;
  description?: string;
  enum?: string[];
  default?: unknown;
  items?: {
    type?: string;
    properties?: Record<string, SchemaProperty>;
    required?: string[];
  };
  properties?: Record<string, SchemaProperty>;
  required?: string[];
  minLength?: number;
  $ref?: string;
}

// ---------------------------------------------------------------------------
// Adjuster handles helpers
// ---------------------------------------------------------------------------

type AdjusterHandleType = 'PHONE' | 'EMAIL';

interface AdjusterHandleRow {
  type: AdjusterHandleType;
  value: string;
}

const isAdjusterHandleType = (value: unknown): value is AdjusterHandleType =>
  value === 'PHONE' || value === 'EMAIL';

const normalizeAdjusterHandles = (value: unknown): AdjusterHandleRow[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((row) => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const record = row as Record<string, unknown>;
      const rawHandleType = record.type ?? record.handle_type;
      const handleType = isAdjusterHandleType(rawHandleType)
        ? rawHandleType
        : 'PHONE';
      const handleValue =
        typeof record.value === 'string' ? record.value : String(record.value || '');
      return {
        type: handleType,
        value: handleValue,
      };
    })
    .filter((row): row is AdjusterHandleRow => row !== null);
};

const normalizeFormDataBeforeSubmit = (
  data: Record<string, unknown>,
): Record<string, unknown> => {
  const normalized = { ...data };
  const handles = normalizeAdjusterHandles(normalized.adjuster_handles).filter(
    (row) => row.value.trim().length > 0,
  );
  if (handles.length > 0) {
    normalized.adjuster_handles = handles;
  } else if ('adjuster_handles' in normalized) {
    normalized.adjuster_handles = [];
  }

  if (typeof normalized.call_outcome === 'string' && normalized.call_success === undefined) {
    const outcome = normalized.call_outcome;
    const successfulOutcomes = new Set([
      'opened_claim',
      'got_claim_info',
      'got_adjuster_contact',
    ]);
    normalized.call_success = successfulOutcomes.has(outcome);
  }

  return normalized;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Initialise default values for a set of schema properties. */
function buildDefaults(
  props: Record<string, SchemaProperty>,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  Object.entries(props).forEach(([key, prop]) => {
    if (prop.default !== undefined) {
      defaults[key] = prop.default;
    } else if (prop.type === 'boolean') {
      defaults[key] = false;
    } else if (prop.type === 'array') {
      defaults[key] = [];
    } else if (prop.type === 'string') {
      defaults[key] = '';
    } else if (prop.type === 'object' && prop.properties) {
      defaults[key] = buildDefaults(prop.properties);
    }
  });
  return defaults;
}

/**
 * Resolve `$ref` references in schema properties using `$defs`.
 *
 * Pydantic emits enum fields as `{ "$ref": "#/$defs/EnumName" }` with the
 * actual type/enum values stored under `schema.$defs.EnumName`. This function
 * walks properties and inlines the referenced definitions so the renderer
 * can treat them as normal properties.
 */
function resolveRefs(
  properties: Record<string, SchemaProperty>,
  defs: Record<string, SchemaProperty> | undefined,
): Record<string, SchemaProperty> {
  if (!defs) return properties;

  return Object.entries(properties).reduce<Record<string, SchemaProperty>>(
    (resolved, [key, prop]) => {
      if (prop.$ref) {
        // e.g. "#/$defs/UpworkMedicalRecordsOutcome" -> "UpworkMedicalRecordsOutcome"
        const refName = prop.$ref.split('/').pop();
        const def = refName ? defs[refName] : undefined;
        if (def) {
          // Merge: the referenced definition provides type/enum, but keep any
          // local overrides (description, default, etc.) from the property itself.
          resolved[key] = {
            ...def,
            ...stripUndefined(prop as unknown as Record<string, unknown>),
            $ref: undefined,
          } as SchemaProperty;
        } else {
          resolved[key] = prop;
        }
      } else if (prop.type === 'object' && prop.properties) {
        resolved[key] = {
          ...prop,
          properties: resolveRefs(prop.properties, defs),
        };
      } else {
        resolved[key] = prop;
      }
      return resolved;
    },
    {},
  );
}

/** Remove keys whose value is undefined so they don't override def values. */
function stripUndefined(
  obj: Record<string, unknown>,
): Record<string, unknown> {
  return Object.entries(obj).reduce<Record<string, unknown>>(
    (clean, [k, v]) => {
      if (v !== undefined) clean[k] = v;
      return clean;
    },
    {},
  );
}

const asText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

const firstNonEmptyText = (...values: unknown[]): string => {
  return values.map((value) => asText(value).trim()).find((text) => text.length > 0) || '';
};

type EvalFormRendererKey = 'bpo_outcome_v1';

const isEvalFormRendererKey = (value: unknown): value is EvalFormRendererKey =>
  value === 'bpo_outcome_v1';

/**
 * Return the set of nested-object property names that should be visible.
 *
 * Convention: when any top-level enum field's current value matches a
 * top-level object property name, that section becomes visible.
 */
function getVisibleSections(
  properties: Record<string, SchemaProperty>,
  formData: Record<string, unknown>,
): Set<string> {
  const visible = new Set<string>();
  const objectKeys = new Set(
    Object.entries(properties)
      .filter(([, p]) => p.type === 'object' && p.properties)
      .map(([k]) => k),
  );

  Object.entries(properties).forEach(([enumName, enumProp]) => {
    if (enumProp.enum && enumProp.enum.length > 0) {
      const val = formData[enumName] as string;
      if (val && objectKeys.has(val)) {
        visible.add(val);
      }
    }
  });

  // If no enum-based match, show all object sections
  if (visible.size === 0 && objectKeys.size > 0) {
    const hasAnyEnum = Object.values(properties).some(
      (p) => p.enum && p.enum.length > 0,
    );
    if (!hasAnyEnum) {
      objectKeys.forEach((k) => visible.add(k));
    }
  }

  return visible;
}

/** Validate all fields and return error messages. */
function validateForm(
  properties: Record<string, SchemaProperty>,
  formData: Record<string, unknown>,
  requiredFields: Record<string, boolean>,
  visibleSections: Set<string>,
): string[] {
  const errors: string[] = [];

  Object.entries(properties).forEach(([name, prop]) => {
    const label = prop.description || name.replace(/_/g, ' ');

    // Required field check
    if (requiredFields[name] && prop.type !== 'object') {
      const val = formData[name];
      if (val === undefined || val === null || val === '') {
        errors.push(`${label} is required`);
      }
    }

    // minLength for string fields
    if (prop.minLength && prop.type === 'string') {
      const val = formData[name] as string;
      if (val && val.length > 0 && val.length < prop.minLength) {
        errors.push(`${label} must be at least ${prop.minLength} characters`);
      }
    }

    // Nested object validation (only for visible sections)
    if (
      prop.type === 'object' &&
      prop.properties &&
      visibleSections.has(name)
    ) {
      const parentData = (formData[name] || {}) as Record<string, unknown>;
      const nestedRequired = (prop.required || []).reduce(
        (acc, f) => ({ ...acc, [f]: true }),
        {} as Record<string, boolean>,
      );
      Object.entries(prop.properties).forEach(([childName, childProp]) => {
        const childLabel =
          childProp.description || childName.replace(/_/g, ' ');
        if (nestedRequired[childName]) {
          const childVal = parentData[childName];
          if (childVal === undefined || childVal === null || childVal === '') {
            errors.push(`${childLabel} is required`);
          }
        }
        if (childProp.minLength && childProp.type === 'string') {
          const childVal = parentData[childName] as string;
          if (
            childVal &&
            childVal.length > 0 &&
            childVal.length < childProp.minLength
          ) {
            errors.push(
              `${childLabel} must be at least ${childProp.minLength} characters`,
            );
          }
        }
      });
    }
  });

  return errors;
}

// ---------------------------------------------------------------------------
// Dark-theme class overrides for shadcn components on this standalone page
// ---------------------------------------------------------------------------

const DARK_INPUT =
  'border-[#1f2a44] bg-[#0a1228] text-white placeholder:text-gray-500 focus-visible:ring-blue-500';
const DARK_SELECT_TRIGGER =
  'border-[#1f2a44] bg-[#0a1228] text-white focus:ring-blue-500';
const DARK_SELECT_CONTENT = 'border-[#1f2a44] bg-[#121936] text-white';
const DARK_LABEL = 'text-[#9aa3b2]';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AgentflowEvalPage: React.FC = () => {
  const { matter_id, approval_id } = useParams<{
    matter_id: string;
    approval_id: string;
  }>();
  const [searchParams] = useSearchParams();

  const {
    data: formConfig,
    isLoading,
    error: fetchError,
  } = useAgentflowGetEvalFormQuery(
    { matterId: matter_id || '', approvalId: approval_id || '' },
    { skip: !matter_id || !approval_id },
  );

  const [submitEval] = useAgentflowSubmitEvalFormMutation();

  const [formData, setFormData] = React.useState<Record<string, unknown>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  // Derived schema data
  const schema = formConfig?.response_schema;
  const defs = schema?.$defs as Record<string, SchemaProperty> | undefined;
  const properties = React.useMemo(
    () =>
      resolveRefs(
        (schema?.properties || {}) as Record<string, SchemaProperty>,
        defs,
      ),
    [schema, defs],
  );
  const requiredFields = React.useMemo(
    () =>
      ((schema?.required || []) as string[]).reduce(
        (acc, field) => ({ ...acc, [field]: true }),
        {} as Record<string, boolean>,
      ),
    [schema],
  );

  const activeRenderer: EvalFormRendererKey | null = isEvalFormRendererKey(formConfig?.ui_renderer)
    ? formConfig.ui_renderer
    : null;
  const useBpoOutcomeRenderer = activeRenderer === 'bpo_outcome_v1';

  const launchContext = (formConfig?.launch_context || {}) as Record<string, unknown>;
  const matterName = firstNonEmptyText(
    searchParams.get('matter_name'),
    launchContext.matter_name,
  );
  const matterDisplay = firstNonEmptyText(matterName, matter_id, launchContext.matter_id) || '—';
  const matterIdDisplay = firstNonEmptyText(searchParams.get('matter_id'), matter_id) || '—';
  const callId = firstNonEmptyText(searchParams.get('call_id'), launchContext.call_id);
  const pendingCallId = firstNonEmptyText(
    searchParams.get('pending_call_id'),
    launchContext.pending_call_id,
  );
  const callIdDisplay = callId || (pendingCallId ? 'Waiting for call...' : '—');
  const toNumber = firstNonEmptyText(searchParams.get('to_number'), launchContext.to_number) || '—';
  const fromNumber = firstNonEmptyText(searchParams.get('from_number'), launchContext.from_number) || '—';

  // Initialise defaults when config loads
  React.useEffect(() => {
    if (Object.keys(properties).length > 0) {
      const defaults = buildDefaults(properties);
      if (useBpoOutcomeRenderer) {
        defaults.claim_benefit_type = defaults.claim_benefit_type || 'COMPREHENSIVE_AUTO';
        defaults.policy_identifier_type = defaults.policy_identifier_type || 'POLICY_NUMBER';
        defaults.adjuster_handles = normalizeAdjusterHandles(defaults.adjuster_handles);
        if (
          Array.isArray(defaults.adjuster_handles)
          && (defaults.adjuster_handles as AdjusterHandleRow[]).length === 0
        ) {
          defaults.adjuster_handles = [{ type: 'PHONE', value: '' }];
        }
      }
      setFormData(defaults);
    }
  }, [properties, useBpoOutcomeRenderer]);

  // Visible nested sections (recomputed on every formData change)
  const visibleSections = React.useMemo(
    () => getVisibleSections(properties, formData),
    [properties, formData],
  );

  // -------------------------------------------------------------------------
  // Field change handlers
  // -------------------------------------------------------------------------
  const handleFieldChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedFieldChange = (
    parentField: string,
    childField: string,
    value: unknown,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...((prev[parentField] as Record<string, unknown>) || {}),
        [childField]: value,
      },
    }));
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!matter_id || !approval_id) return;

    const errors = validateForm(
      properties,
      formData,
      requiredFields,
      visibleSections,
    );
    if (errors.length > 0) {
      toast.error('Please fix the following errors', {
        description: errors.join('. '),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await submitEval({
        matterId: matter_id,
        approvalId: approval_id,
        submitEvalFormRequest: {
          action: 'submit',
          modified_data: normalizeFormDataBeforeSubmit(formData),
        },
      }).unwrap();
      toast.success('Evaluation submitted successfully');
      setSubmitted(true);
    } catch (err: unknown) {
      const message =
        (err as { data?: { detail?: string } })?.data?.detail ||
        'Failed to submit evaluation';
      toast.error('Submission failed', { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Field renderers
  // -------------------------------------------------------------------------

  const renderField = (
    name: string,
    property: SchemaProperty,
    required: boolean,
    value: unknown,
    onChange: (val: unknown) => void,
  ) => {
    const { type, description, enum: enumValues } = property;
    const label = description || name.replace(/_/g, ' ');
    const fieldId = `field-${name}`;

    // Array of objects -> adjuster handles
    if (
      name === 'adjuster_handles'
      && type === 'array'
      && property.items?.type === 'object'
    ) {
      const handles = normalizeAdjusterHandles(value);
      const displayHandles =
        handles.length > 0 ? handles : [{ type: 'PHONE' as const, value: '' }];

      const updateHandle = (
        index: number,
        field: keyof AdjusterHandleRow,
        nextValue: string,
      ) => {
        const current = normalizeAdjusterHandles(formData[name]);
        const base = current.length > 0 ? current : [{ type: 'PHONE' as const, value: '' }];
        const updated = base.map((row, rowIndex) => {
          if (rowIndex !== index) {
            return row;
          }
          if (field === 'type') {
            return {
              ...row,
              type: isAdjusterHandleType(nextValue) ? nextValue : ('PHONE' as const),
            };
          }
          return {
            ...row,
            value: nextValue,
          };
        });
        handleFieldChange(name, updated);
      };

      const addHandle = () => {
        const current = normalizeAdjusterHandles(formData[name]);
        handleFieldChange(name, [...current, { type: 'PHONE' as const, value: '' }]);
      };

      const removeHandle = (index: number) => {
        const current = normalizeAdjusterHandles(formData[name]);
        handleFieldChange(
          name,
          current.filter((_: AdjusterHandleRow, rowIndex: number) => rowIndex !== index),
        );
      };

      return (
        <div key={name}>
          <div className="block text-xs text-[#9aa3b2] mb-1 capitalize">
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </div>
          <div className="space-y-2">
            {displayHandles.map((handle, index) => (
              <div
                key={`${name}-${index}`}
                className="grid grid-cols-12 gap-2 items-center"
              >
                <div className="col-span-4">
                  <select
                    aria-label="Handle type"
                    value={handle.type}
                    onChange={(event) =>
                      updateHandle(index, 'type', event.target.value)
                    }
                    className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="PHONE">Phone</option>
                    <option value="EMAIL">Email</option>
                  </select>
                </div>
                <div className="col-span-7">
                  <input
                    aria-label="Handle value"
                    value={handle.value}
                    onChange={(event) => updateHandle(index, 'value', event.target.value)}
                    placeholder={
                      handle.type === 'PHONE'
                        ? '+1 (555) 555-5555'
                        : 'name@example.com'
                    }
                    className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    onClick={() => removeHandle(index)}
                    className="px-2 py-2 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
                    aria-label="Remove contact"
                  >
                    -
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addHandle}
              className="px-3 py-2 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
            >
              + Add Contact
            </button>
          </div>
        </div>
      );
    }

    // Enum -> Select
    if (enumValues && enumValues.length > 0) {
      return (
        <div key={name} className="space-y-2">
          <Label htmlFor={fieldId} className={DARK_LABEL}>
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </Label>
          <Select
            value={(value as string) || ''}
            onValueChange={(val) => onChange(val)}
          >
            <SelectTrigger id={fieldId} className={DARK_SELECT_TRIGGER}>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent className={DARK_SELECT_CONTENT}>
              {enumValues.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt.replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Boolean -> checkbox
    if (type === 'boolean') {
      return (
        <div key={name} className="flex items-center gap-2">
          <input
            type="checkbox"
            id={fieldId}
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded border-[#1f2a44] bg-[#0a1228] text-blue-500"
          />
          <Label htmlFor={fieldId} className="text-white">
            {label}
          </Label>
        </div>
      );
    }

    // Number / integer
    if (type === 'integer' || type === 'number') {
      return (
        <div key={name} className="space-y-2">
          <Label htmlFor={fieldId} className={DARK_LABEL}>
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </Label>
          <Input
            type="number"
            id={fieldId}
            value={(value as number) ?? ''}
            onChange={(e) =>
              onChange(e.target.value ? Number(e.target.value) : null)
            }
            required={required}
            className={DARK_INPUT}
          />
        </div>
      );
    }

    // String with minLength -> Textarea
    if (type === 'string' && property.minLength && property.minLength >= 20) {
      const strVal = (value as string) || '';
      return (
        <div key={name} className="space-y-2">
          <Label htmlFor={fieldId} className={DARK_LABEL}>
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
            <span className="text-gray-600 ml-2 font-normal">
              (min {property.minLength} characters)
            </span>
          </Label>
          <Textarea
            id={fieldId}
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            rows={3}
            className={DARK_INPUT}
          />
          {strVal.length > 0 && strVal.length < property.minLength && (
            <p className="text-xs text-red-400">
              {property.minLength - strVal.length} more character
              {property.minLength - strVal.length !== 1 ? 's' : ''} needed
            </p>
          )}
        </div>
      );
    }

    // Default -> text Input
    return (
      <div key={name} className="space-y-2">
        <Label htmlFor={fieldId} className={DARK_LABEL}>
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </Label>
        <Input
          type="text"
          id={fieldId}
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className={DARK_INPUT}
        />
      </div>
    );
  };

  /** Render a nested object section as a fieldset. */
  const renderNestedFieldset = (
    parentName: string,
    property: SchemaProperty,
  ) => {
    if (!property.properties) return null;
    const parentData = (formData[parentName] || {}) as Record<string, unknown>;
    const nestedRequired = (property.required || []).reduce(
      (acc, f) => ({ ...acc, [f]: true }),
      {} as Record<string, boolean>,
    );

    return (
      <fieldset
        key={parentName}
        className="border border-[#1f2a44] rounded-lg p-4 space-y-4"
      >
        <legend className="text-sm font-medium text-white px-2 capitalize">
          {property.description || parentName.replace(/_/g, ' ')}
        </legend>
        {Object.entries(property.properties).map(([childName, childProp]) =>
          renderField(
            `${parentName}.${childName}`,
            childProp,
            !!nestedRequired[childName],
            parentData[childName],
            (val) => handleNestedFieldChange(parentName, childName, val),
          ),
        )}
      </fieldset>
    );
  };

  // -------------------------------------------------------------------------
  // State-driven renders
  // -------------------------------------------------------------------------

  let error: string | null = null;
  if (!matter_id || !approval_id) {
    error = 'Missing matter_id or approval_id in URL';
  } else if (fetchError) {
    error = 'Failed to load evaluation form';
  }

  if (isLoading) {
    return (
      <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
        <div className="flex items-center gap-2 text-white">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading evaluation form...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-[#1f2a44] bg-gradient-to-b from-[#121936] to-[#0f1731]">
          <CardHeader>
            <CardTitle className="text-red-400">Error</CardTitle>
            <CardDescription className="text-[#9aa3b2]">
              {error}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-[#1f2a44] bg-gradient-to-b from-[#121936] to-[#0f1731] text-center">
          <CardHeader>
            <div className="text-4xl mb-2">&#10003;</div>
            <CardTitle className="text-green-400">
              Evaluation Submitted
            </CardTitle>
            <CardDescription className="text-[#9aa3b2]">
              Thank you! Your evaluation has been recorded.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (useBpoOutcomeRenderer) {
    const bpoHandles = normalizeAdjusterHandles(formData.adjuster_handles);

    const addBpoHandle = () => {
      handleFieldChange('adjuster_handles', [
        ...bpoHandles,
        { type: 'PHONE' as const, value: '' },
      ]);
    };

    const removeBpoHandle = (index: number) => {
      handleFieldChange(
        'adjuster_handles',
        bpoHandles.filter((_, rowIndex) => rowIndex !== index),
      );
    };

    const updateBpoHandle = (
      index: number,
      field: 'type' | 'value',
      nextValue: string,
    ) => {
      handleFieldChange(
        'adjuster_handles',
        bpoHandles.map((row, rowIndex) => {
          if (rowIndex !== index) {
            return row;
          }
          if (field === 'type') {
            return {
              ...row,
              type: isAdjusterHandleType(nextValue) ? nextValue : ('PHONE' as const),
            };
          }
          return {
            ...row,
            value: nextValue,
          };
        }),
      );
    };

    return (
      <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
        <div className="w-full max-w-2xl rounded-xl border border-[#1f2a44] shadow-2xl bg-gradient-to-b from-[#121936] to-[#0f1731] overflow-hidden">
          <div className="px-6 py-5 border-b border-[#1f2a44] bg-[radial-gradient(1200px_300px_at_-10%_-20%,rgba(34,197,94,0.18),transparent_60%)]">
            <h2 className="text-lg font-semibold text-white">Log BPO Call Outcome</h2>
            <p className="text-xs text-[#9aa3b2] mt-1">
              Please record the result of your call. Only call outcome is required.
            </p>
          </div>

          <div className="p-6">
            <div className="mb-4 rounded-lg border border-[#1f2a44] p-3 text-sm text-[#9aa3b2] bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="text-white font-medium">Matter</span>
                  <div>{matterDisplay}</div>
                  <div className="text-xs text-[#7f8aa3]">{matterIdDisplay}</div>
                </div>
                <div>
                  <span className="text-white font-medium">Call ID</span>
                  <div>{callIdDisplay}</div>
                </div>
                <div>
                  <span className="text-white font-medium">To</span>
                  <div>{toNumber}</div>
                </div>
                <div>
                  <span className="text-white font-medium">From</span>
                  <div>{fromNumber}</div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <div className="block text-xs text-[#9aa3b2] mb-1">Call Outcome</div>
                <select
                  id="call_outcome"
                  value={asText(formData.call_outcome)}
                  onChange={(event) => handleFieldChange('call_outcome', event.target.value)}
                  required
                  aria-label="Select the outcome of the call"
                  className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select one</option>
                  <option value="opened_claim">Opened Claim</option>
                  <option value="got_claim_info">Got Claim Info</option>
                  <option value="got_adjuster_contact">Got Adjuster Name + Contact</option>
                  <option value="not_successful">Call not successful</option>
                </select>
              </div>

              <div>
                <div className="block text-xs text-[#9aa3b2] mb-1">Claim Number</div>
                <input
                  id="claim_number"
                  value={asText(formData.claim_number)}
                  onChange={(event) => handleFieldChange('claim_number', event.target.value)}
                  aria-label="Claim Number"
                  className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <div className="block text-xs text-[#9aa3b2] mb-1">Benefit</div>
                <select
                  id="claim_benefit_type"
                  value={asText(formData.claim_benefit_type)}
                  onChange={(event) =>
                    handleFieldChange('claim_benefit_type', event.target.value)
                  }
                  className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">(none)</option>
                  <option value="PERSONAL_INJURY_PROTECTION">
                    Personal Injury Protection (PIP)
                  </option>
                  <option value="MEDICAL_PAYMENTS">Medical Payments (MedPay)</option>
                  <option value="UNINSURED_MOTORIST">Uninsured Motorist (UM)</option>
                  <option value="UNDER_INSURED_MOTORIST">Underinsured Motorist (UIM)</option>
                  <option value="BODILY_INJURY_LIABILITY">Bodily Injury Liability (BI)</option>
                  <option value="PROPERTY_DAMAGE_LIABILITY">
                    Property Damage Liability (PD)
                  </option>
                  <option value="COMPREHENSIVE_AUTO">Comprehensive (Auto)</option>
                  <option value="COLLISION_AUTO">Collision (Auto)</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div>
                <div className="block text-xs text-[#9aa3b2] mb-1">Policy Identifier</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select
                    id="policy_identifier_type"
                    value={asText(formData.policy_identifier_type)}
                    onChange={(event) =>
                      handleFieldChange('policy_identifier_type', event.target.value)
                    }
                    className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                    aria-label="Policy Identifier Type"
                  >
                    <option value="POLICY_NUMBER">Policy Number</option>
                    <option value="MEMBER_ID">Member ID</option>
                    <option value="GROUP_NUMBER">Group Number</option>
                    <option value="OTHER">Other</option>
                  </select>
                  <input
                    id="policy_number"
                    value={asText(formData.policy_number)}
                    onChange={(event) => handleFieldChange('policy_number', event.target.value)}
                    aria-label="Policy Identifier"
                    className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <div className="block text-xs text-[#9aa3b2] mb-1">Adjuster Name</div>
                <input
                  id="adjuster_name"
                  value={asText(formData.adjuster_name)}
                  onChange={(event) => handleFieldChange('adjuster_name', event.target.value)}
                  aria-label="Adjuster Name"
                  className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <div className="block text-xs text-[#9aa3b2] mb-1">Adjuster Contacts</div>
                <div className="space-y-2">
                  {bpoHandles.map((handle, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <select
                          aria-label="Handle type"
                          value={handle.type}
                          onChange={(event) =>
                            updateBpoHandle(index, 'type', event.target.value)
                          }
                          className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                        >
                          <option value="PHONE">Phone</option>
                          <option value="EMAIL">Email</option>
                        </select>
                      </div>
                      <div className="col-span-7">
                        <input
                          aria-label="Handle value"
                          value={handle.value}
                          onChange={(event) =>
                            updateBpoHandle(index, 'value', event.target.value)
                          }
                          placeholder={
                            handle.type === 'PHONE'
                              ? '+1 (555) 555-5555'
                              : 'name@example.com'
                          }
                          className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={() => removeBpoHandle(index)}
                          className="px-2 py-2 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
                          aria-label="Remove contact"
                        >
                          −
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addBpoHandle}
                    className="px-3 py-2 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
                  >
                    + Add Contact
                  </button>
                </div>
              </div>

              <div>
                <div className="block text-xs text-[#9aa3b2] mb-1">Call Issues</div>
                <textarea
                  id="call_issues"
                  value={asText(formData.call_issues)}
                  onChange={(event) => handleFieldChange('call_issues', event.target.value)}
                  aria-label="Call Issues"
                  rows={3}
                  placeholder="Note any issues that occurred during the call (optional)"
                  className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 disabled:opacity-50"
                  aria-label="Save Outcome"
                >
                  {isSubmitting ? 'Saving…' : 'Save Outcome'}
                </button>
              </div>

              <p className="text-xs text-[#9aa3b2]">
                This will be added to the Voice AI run as a BPO action.
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl border-[#1f2a44] bg-gradient-to-b from-[#121936] to-[#0f1731]">
        <CardHeader className="border-b border-[#1f2a44]">
          <CardTitle className="text-white">
            {formConfig?.form_title || 'Call Evaluation'}
          </CardTitle>
          <CardDescription className="text-[#9aa3b2]">
            {formConfig?.form_description ||
              'Please complete this evaluation form.'}
          </CardDescription>
          {formConfig?.task_id && (
            <div className="mt-1 text-xs text-[#9aa3b2]">
              Task:{' '}
              <span className="font-mono text-white">
                {formConfig.task_id}
              </span>
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {Object.keys(properties).length > 0 ? (
              Object.entries(properties).map(([name, prop]) => {
                // Nested object -> conditional fieldset
                if (prop.type === 'object' && prop.properties) {
                  if (!visibleSections.has(name)) return null;
                  return renderNestedFieldset(name, prop);
                }
                // Flat field
                return renderField(
                  name,
                  prop,
                  !!requiredFields[name],
                  formData[name],
                  (val) => handleFieldChange(name, val),
                );
              })
            ) : (
              <div className="space-y-2">
                <Label htmlFor="notes" className={DARK_LABEL}>
                  Notes
                </Label>
                <Textarea
                  id="notes"
                  value={(formData.notes as string) || ''}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  rows={4}
                  placeholder="Enter any notes about the call..."
                  className={DARK_INPUT}
                />
              </div>
            )}

            <div className="pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Evaluation'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentflowEvalPage;
